import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Sparkles, Clock, X } from 'lucide-react';
import { TreatmentRecord } from '../../../types/treatment';

interface TreatmentDetailsSectionProps {
  record: Partial<TreatmentRecord>;
  onChange: (updates: Partial<TreatmentRecord>) => void;
  mode?: 'view' | 'edit' | 'create';
}

const treatmentMenuOptions = [
  'ドライヘッドスパ 30分',
  'ドライヘッドスパ 60分',
  'ドライヘッドスパ 90分',
  'プレミアムヘッドスパ 90分',
  'ヘッド＋ショルダー 60分',
  'ヘッド＋フェイシャル 90分',
  'フルボディリラクゼーション 120分',
];

const commonTechniques = [
  '指圧',
  'ストレッチ',
  'リンパドレナージュ',
  'ツボ押し',
  'オイルマッサージ',
  'ホットストーン',
  'アロマテラピー',
  'クレニオセイクラル',
];

const focusAreaOptions = [
  '頭頂部',
  '側頭部',
  '後頭部',
  '首・肩',
  '目の周り',
  '顔全体',
  '背中',
  '腰',
  '脚',
  '全身',
];

export const TreatmentDetailsSection: React.FC<TreatmentDetailsSectionProps> = ({
  record,
  onChange,
  mode = 'view',
}) => {
  const [customTechnique, setCustomTechnique] = useState('');
  const [customFocusArea, setCustomFocusArea] = useState('');

  const handleFieldChange = (field: keyof TreatmentRecord, value: unknown) => {
    onChange({ [field]: value });
  };

  const toggleArrayItem = (field: keyof TreatmentRecord, item: string) => {
    const currentArray = (record[field] as string[]) || [];
    const newArray = currentArray.includes(item)
      ? currentArray.filter(i => i !== item)
      : [...currentArray, item];
    handleFieldChange(field, newArray);
  };

  const addCustomItem = (field: keyof TreatmentRecord, value: string, setValue: (v: string) => void) => {
    if (!value.trim()) return;
    const currentArray = (record[field] as string[]) || [];
    if (!currentArray.includes(value.trim())) {
      handleFieldChange(field, [...currentArray, value.trim()]);
    }
    setValue('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          施術詳細
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4">
          {/* 施術メニュー & 施術時間 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">施術メニュー <span className="text-destructive">*</span></label>
              <Input
                value={record.treatmentMenu || ''}
                onChange={(e) => handleFieldChange('treatmentMenu', e.target.value)}
                disabled={mode === 'view'}
                placeholder="選択または入力"
                list="treatmentMenuOptions"
              />
              <datalist id="treatmentMenuOptions">
                {treatmentMenuOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">施術時間 <span className="text-destructive">*</span></label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={record.treatmentDuration || ''}
                  onChange={(e) => handleFieldChange('treatmentDuration', parseInt(e.target.value) || 0)}
                  disabled={mode === 'view'}
                  className="pl-9"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-muted-foreground">分</span>
              </div>
            </div>
          </div>

          {/* 使用した技術・手技 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">使用した技術・手技</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {commonTechniques.map((technique) => (
                <Badge
                  key={technique}
                  variant={(record.techniques || []).includes(technique) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => mode !== 'view' && toggleArrayItem('techniques', technique)}
                >
                  {technique}
                </Badge>
              ))}
            </div>
            {mode !== 'view' && (
              <div className="flex gap-2">
                <Input
                  value={customTechnique}
                  onChange={(e) => setCustomTechnique(e.target.value)}
                  placeholder="カスタム入力"
                  className="text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && addCustomItem('techniques', customTechnique, setCustomTechnique)}
                />
                <Button
                  size="sm"
                  onClick={() => addCustomItem('techniques', customTechnique, setCustomTechnique)}
                >
                  追加
                </Button>
              </div>
            )}
            {(record.techniques || []).some(t => !commonTechniques.includes(t)) && (
              <div className="flex flex-wrap gap-2">
                {(record.techniques || []).filter(t => !commonTechniques.includes(t)).map((technique) => (
                  <Badge
                    key={technique}
                    variant="secondary"
                    className="cursor-pointer"
                  >
                    {technique}
                    {mode !== 'view' && (
                      <X
                        className="ml-1 h-3 w-3"
                        onClick={() => toggleArrayItem('techniques', technique)}
                      />
                    )}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* 重点施術部位 & 圧力の強さ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">重点施術部位</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {focusAreaOptions.map((area) => (
                  <Badge
                    key={area}
                    variant={(record.focusAreas || []).includes(area) ? "secondary" : "outline"}
                    className="cursor-pointer"
                    onClick={() => mode !== 'view' && toggleArrayItem('focusAreas', area)}
                  >
                    {area}
                  </Badge>
                ))}
              </div>
              {mode !== 'view' && (
                <div className="flex gap-2">
                  <Input
                    value={customFocusArea}
                    onChange={(e) => setCustomFocusArea(e.target.value)}
                    placeholder="カスタム入力"
                    className="text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addCustomItem('focusAreas', customFocusArea, setCustomFocusArea)}
                  />
                  <Button
                    size="sm"
                    onClick={() => addCustomItem('focusAreas', customFocusArea, setCustomFocusArea)}
                  >
                    追加
                  </Button>
                </div>
              )}
              {(record.focusAreas || []).some(a => !focusAreaOptions.includes(a)) && (
                <div className="flex flex-wrap gap-2">
                  {(record.focusAreas || []).filter(a => !focusAreaOptions.includes(a)).map((area) => (
                    <Badge
                      key={area}
                      variant="secondary"
                      className="cursor-pointer"
                    >
                      {area}
                      {mode !== 'view' && (
                        <X
                          className="ml-1 h-3 w-3"
                          onClick={() => toggleArrayItem('focusAreas', area)}
                        />
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">圧力の強さ: {record.pressure || 5}</label>
              <div className="px-2">
                <Slider
                  value={[record.pressure || 5]}
                  onValueChange={(value) => handleFieldChange('pressure', value[0])}
                  disabled={mode === 'view'}
                  min={1}
                  max={10}
                  step={1}
                  className="mt-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>弱</span>
                  <span>中</span>
                  <span>強</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};